function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EMPTY_ARRAY } from '@deephaven/utils';
import { Button } from "../Button.js";
import SearchInput from "../SearchInput.js";
import "./DashboardList.css";
import { GLOBAL_SHORTCUTS } from "../shortcuts/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Display a search field and a list of dashboard tabs
 * @param props The tabs and handlers to use for this list
 * @returns A JSX element for the list of dashboard tabs, along with search
 */
export function DashboardList(props) {
  var onSelect = props.onSelect,
    _props$tabs = props.tabs,
    tabs = _props$tabs === void 0 ? EMPTY_ARRAY : _props$tabs;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    focusedIndex = _useState4[0],
    setFocusedIndex = _useState4[1];
  var searchField = useRef(null);
  var listRef = useRef(null);
  var itemRefs = useRef([]);
  useEffect(() => {
    var _searchField$current;
    (_searchField$current = searchField.current) === null || _searchField$current === void 0 || _searchField$current.focus();
  }, []);
  useEffect(() => {
    setFocusedIndex(0);
  }, [searchText]);
  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      var _itemRefs$current$foc;
      (_itemRefs$current$foc = itemRefs.current[focusedIndex]) === null || _itemRefs$current$foc === void 0 || _itemRefs$current$foc.scrollIntoView({
        behavior: 'auto',
        block: 'nearest'
      });
    }
  }, [focusedIndex]);
  var handleSearchChange = useCallback(e => {
    setSearchText(e.target.value);
  }, []);
  var handleTabSelect = useCallback(tab => {
    onSelect(tab);
  }, [onSelect]);
  var handleMouseDown = useCallback(event => {
    // Prevent mousedown from taking focus away from the search input
    event.preventDefault();
  }, []);
  var filteredTabs = useMemo(() => tabs.filter(tab => tab.title.toLowerCase().includes(searchText.toLowerCase())), [searchText, tabs]).sort((a, b) => {
    var _a$title$localeCompar;
    return (_a$title$localeCompar = a.title.localeCompare(b.title)) !== null && _a$title$localeCompar !== void 0 ? _a$title$localeCompar : 0;
  });
  var handleSearchKeyDown = useCallback(event => {
    if (event.key === 'ArrowDown' && filteredTabs.length > 0) {
      event.preventDefault();
      setFocusedIndex(prev => prev === -1 ? 0 : (prev + 1) % filteredTabs.length);
    } else if (event.key === 'ArrowUp' && filteredTabs.length > 0) {
      event.preventDefault();
      setFocusedIndex(prev => {
        if (prev === -1) return filteredTabs.length - 1;
        return (prev - 1 + filteredTabs.length) % filteredTabs.length;
      });
    } else if (event.key === 'Enter' && filteredTabs.length > 0) {
      event.preventDefault();
      var selectedIndex = focusedIndex >= 0 ? focusedIndex : 0;
      handleTabSelect(filteredTabs[selectedIndex]);
    } else if (event.key === 'Tab') {
      event.preventDefault();
    }
  }, [filteredTabs, focusedIndex, handleTabSelect]);
  var tabElements = useMemo(() => filteredTabs.map((tab, index) => {
    var _tab$key;
    return /*#__PURE__*/_jsx("li", {
      ref: el => {
        itemRefs.current[index] = el;
      },
      onMouseDown: handleMouseDown,
      children: /*#__PURE__*/_jsxs(Button, {
        kind: "ghost",
        "data-testid": "dashboard-list-item-".concat((_tab$key = tab.key) !== null && _tab$key !== void 0 ? _tab$key : '', "-button"),
        onClick: () => handleTabSelect(tab),
        className: focusedIndex === index ? 'focused' : '',
        style: {
          transition: 'none'
        },
        children: [tab.icon ? /*#__PURE__*/_jsx("span", {
          className: "dashboard-list-item-icon",
          children: /*#__PURE__*/React.isValidElement(tab.icon) ? tab.icon : /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: tab.icon
          })
        }) : null, tab.title]
      })
    }, tab.key);
  }), [filteredTabs, handleTabSelect, focusedIndex, handleMouseDown]);
  var errorElement = useMemo(() => tabElements.length === 0 ? /*#__PURE__*/_jsx("span", {
    children: "No open dashboard found."
  }) : null, [tabElements]);
  return /*#__PURE__*/_jsxs("div", {
    className: "dashboard-list-container d-flex flex-column",
    children: [/*#__PURE__*/_jsx("div", {
      className: "dashboard-list-header",
      children: /*#__PURE__*/_jsx(SearchInput, {
        value: searchText,
        placeholder: "Find open dashboard",
        endPlaceholder: GLOBAL_SHORTCUTS.OPEN_DASHBOARD_LIST.getDisplayText(),
        onChange: handleSearchChange,
        onKeyDown: handleSearchKeyDown,
        ref: searchField
      })
    }), /*#__PURE__*/_jsxs("ul", {
      className: "dashboard-list flex-grow-1",
      ref: listRef,
      children: [errorElement && /*#__PURE__*/_jsx("li", {
        className: "dashboard-list-message",
        children: errorElement
      }), !errorElement && tabElements]
    })]
  });
}
export default DashboardList;
//# sourceMappingURL=DashboardList.js.map